import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Paper, Typography, Button, Grid, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Tabs, Tab, Alert, Card, CardContent,
  Divider, Stack, Avatar, useTheme
} from '@mui/material';
import {
  LocalOffer as DiscountIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  HourglassEmpty as PendingIcon,
  Receipt as InvoiceIcon,
  Lock as LockIcon,
  Search as SearchIcon,
  TrendingDown as DiscountTrendIcon,
  AccountBalanceWallet as WalletIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { CircularProgress } from '@mui/material';
import GenericPage from '../../Commun/components/GenericPage';
import { blobticMessage } from '../../Commun/components/BlobticMessage';
import { academyStore } from '../services/academyStore';
import { academyApi } from '../services/academyApi';
import { DiscountRequest } from '../types/academy';
import { useLanguage } from '../../Commun/contexts/LanguageContext';
import { useSystemParameters } from '../../Commun/contexts/SystemParametersContext';
import { useAuth } from '../../Commun/contexts/AuthContext';

export const DiscountValidationsPage: React.FC = () => {
  const { t, language } = useLanguage();
  const theme = useTheme();
  const { user } = useAuth();
  const { getParam } = useSystemParameters();
  const currencySymbol = getParam('Currency_Symbol', 'DZD');

  const [, setTick] = useState(0);
  const [activeTab, setActiveTab] = useState<'ALL' | 'Pending' | 'Approved' | 'Rejected'>('Pending');
  const [searchTerm, setSearchTerm] = useState('');
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Modals state
  const [selectedRequest, setSelectedRequest] = useState<DiscountRequest | null>(null);
  const [openApproveModal, setOpenApproveModal] = useState(false);
  const [openRejectModal, setOpenRejectModal] = useState(false);
  const [rejectComment, setRejectComment] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const refreshData = async () => {
    setIsRefreshing(true);
    try {
      await academyStore._refresh('academy_discount_request', () => academyApi.getDiscountRequests(false));
      await academyStore._refresh('academy_invoice', () => academyApi.getInvoices(false));
      setTick(t => t + 1);
    } finally {
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    const unsubscribe = academyStore.subscribe(() => setTick(t => t + 1));
    refreshData();
    return unsubscribe;
  }, []);

  const isUserAdmin = Boolean(
    user?.isAdmin ||
    user?.profileId === 1 ||
    (user?.profileLib && user.profileLib.toLowerCase().includes('admin')) ||
    (user?.profileLib && user.profileLib.toLowerCase().includes('direct'))
  );

  const discountRequests = academyStore.getDiscountRequests();

  // Statistics
  const pendingRequests = useMemo(() => discountRequests.filter(r => r.status === 'Pending'), [discountRequests]);
  const approvedRequests = useMemo(() => discountRequests.filter(r => r.status === 'Approved'), [discountRequests]);
  const rejectedRequests = useMemo(() => discountRequests.filter(r => r.status === 'Rejected'), [discountRequests]);

  const totalPendingDiscountAmount = useMemo(() => {
    return pendingRequests.reduce((sum, r) => sum + (r.discount_amount || 0), 0);
  }, [pendingRequests]);

  const filteredRequests = useMemo(() => {
    return discountRequests.filter(r => {
      if (activeTab !== 'ALL' && r.status !== activeTab) return false;
      if (searchTerm.trim()) {
        const q = searchTerm.toLowerCase();
        const student = (r.student_name || '').toLowerCase();
        const invNum = (r.invoice_number || '').toLowerCase();
        const code = (r.discount_code || '').toLowerCase();
        const justification = (r.justification || '').toLowerCase();
        const requester = (r.requested_by_name || '').toLowerCase();
        return student.includes(q) || invNum.includes(q) || code.includes(q) || justification.includes(q) || requester.includes(q);
      }
      return true;
    });
  }, [discountRequests, activeTab, searchTerm]);

  // Actions
  const handleOpenApprove = (req: DiscountRequest) => {
    setSelectedRequest(req);
    setOpenApproveModal(true);
  };

  const handleOpenReject = (req: DiscountRequest) => {
    setSelectedRequest(req);
    setRejectComment('');
    setOpenRejectModal(true);
  };

  const handleConfirmApprove = async () => {
    if (!selectedRequest) return;
    setActionLoading(true);
    try {
      const res = await academyStore.approveDiscountRequest(selectedRequest.id);
      if (res.success) {
        blobticMessage.success(
          t('common.messages.success'),
          t('academy.discount_validations.approve_success', selectedRequest.discount_percent, selectedRequest.invoice_number)
        );
        setOpenApproveModal(false);
        await refreshData();
      } else {
        blobticMessage.error(t('common.error'), res.message || t('academy.discount_validations.approve_error'));
      }
    } finally {
      setActionLoading(false);
    }
  };

  const handleConfirmReject = async () => {
    if (!selectedRequest) return;
    if (!rejectComment.trim()) {
      blobticMessage.error(t('common.error'), t('academy.discount_validations.dialog_reject_required'));
      return;
    }
    setActionLoading(true);
    try {
      const res = await academyStore.rejectDiscountRequest(selectedRequest.id, rejectComment.trim());
      if (res.success) {
        blobticMessage.info(
          t('academy.discount_validations.reject_info_title'),
          t('academy.discount_validations.reject_info_msg', selectedRequest.invoice_number)
        );
        setOpenRejectModal(false);
        await refreshData();
      } else {
        blobticMessage.error(t('common.error'), res.message || t('academy.discount_validations.reject_error'));
      }
    } finally {
      setActionLoading(false);
    }
  };

  // If user is not admin, deny access
  if (!isUserAdmin) {
    return (
      <GenericPage
        title={t('academy.discount_validations.title')}
        icon={<DiscountIcon sx={{ color: 'primary.main' }} />}
        footerEnabled={false}
      >
        <Box sx={{ p: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <Paper
            elevation={3}
            sx={{
              p: 4,
              maxWidth: 500,
              textAlign: 'center',
              borderRadius: 3,
              border: `1px solid ${theme.palette.divider}`
            }}
          >
            <Avatar sx={{ width: 64, height: 64, bgcolor: 'error.light', color: 'error.main', mx: 'auto', mb: 2 }}>
              <LockIcon sx={{ fontSize: 36 }} />
            </Avatar>
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              {t('academy.discount_validations.access_denied_title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t('academy.discount_validations.access_denied_desc')}
            </Typography>
          </Paper>
        </Box>
      </GenericPage>
    );
  }

  return (
    <GenericPage
      title={t('academy.discount_validations.title')}
      icon={<DiscountIcon sx={{ color: 'primary.main' }} />}
      footerEnabled={false}
      additionalActions={
        <Button
          variant="outlined"
          color="primary"
          startIcon={isRefreshing ? <CircularProgress size={18} color="inherit" /> : <RefreshIcon />}
          onClick={refreshData}
          disabled={isRefreshing}
          sx={{ borderRadius: 2 }}
        >
          {t('common.actions.refresh')}
        </Button>
      }
    >
      <Box sx={{ p: 2 }}>
        {/* KPI Stats Cards Header */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card
              variant="outlined"
              sx={{
                borderRadius: 2.5,
                bgcolor: activeTab === 'Pending' ? 'warning.lighter' : 'background.paper',
                borderColor: activeTab === 'Pending' ? 'warning.main' : 'divider',
                cursor: 'pointer',
                transition: 'transform 0.2s',
                '&:hover': { transform: 'translateY(-3px)' }
              }}
              onClick={() => setActiveTab('Pending')}
            >
              <CardContent sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight="bold">
                    {t('academy.discount_validations.kpi_pending')}
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="warning.main">
                    {pendingRequests.length}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'warning.light', color: 'warning.contrastText', width: 44, height: 44 }}>
                  <PendingIcon />
                </Avatar>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card
              variant="outlined"
              sx={{
                borderRadius: 2.5,
                bgcolor: activeTab === 'Approved' ? 'success.lighter' : 'background.paper',
                borderColor: activeTab === 'Approved' ? 'success.main' : 'divider',
                cursor: 'pointer',
                transition: 'transform 0.2s',
                '&:hover': { transform: 'translateY(-3px)' }
              }}
              onClick={() => setActiveTab('Approved')}
            >
              <CardContent sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight="bold">
                    {t('academy.discount_validations.kpi_approved')}
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="success.main">
                    {approvedRequests.length}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'success.light', color: 'success.contrastText', width: 44, height: 44 }}>
                  <CheckCircleIcon />
                </Avatar>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card
              variant="outlined"
              sx={{
                borderRadius: 2.5,
                bgcolor: activeTab === 'Rejected' ? 'error.lighter' : 'background.paper',
                borderColor: activeTab === 'Rejected' ? 'error.main' : 'divider',
                cursor: 'pointer',
                transition: 'transform 0.2s',
                '&:hover': { transform: 'translateY(-3px)' }
              }}
              onClick={() => setActiveTab('Rejected')}
            >
              <CardContent sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight="bold">
                    {t('academy.discount_validations.kpi_rejected')}
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="error.main">
                    {rejectedRequests.length}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'error.light', color: 'error.contrastText', width: 44, height: 44 }}>
                  <CancelIcon />
                </Avatar>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card
              variant="outlined"
              sx={{
                borderRadius: 2.5,
                bgcolor: 'background.paper',
                borderColor: 'divider'
              }}
            >
              <CardContent sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight="bold">
                    {t('academy.discount_validations.kpi_total_pending')}
                  </Typography>
                  <Typography variant="h5" fontWeight="bold" color="primary.main">
                    {currencySymbol}{totalPendingDiscountAmount.toLocaleString()}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'primary.light', color: 'primary.contrastText', width: 44, height: 44 }}>
                  <WalletIcon />
                </Avatar>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Filters & Search Toolbar */}
        <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2.5 }}>
          <Grid container spacing={2} alignItems="center" justifyContent="space-between">
            <Grid item xs={12} md={7}>
              <Tabs
                value={activeTab}
                onChange={(_, val) => setActiveTab(val)}
                indicatorColor="primary"
                textColor="primary"
                sx={{ minHeight: 44 }}
              >
                <Tab value="Pending" label={t('academy.discount_validations.tab_pending', pendingRequests.length)} sx={{ minHeight: 44, textTransform: 'none', fontWeight: 'bold' }} />
                <Tab value="Approved" label={t('academy.discount_validations.tab_approved', approvedRequests.length)} sx={{ minHeight: 44, textTransform: 'none', fontWeight: 'bold' }} />
                <Tab value="Rejected" label={t('academy.discount_validations.tab_rejected', rejectedRequests.length)} sx={{ minHeight: 44, textTransform: 'none', fontWeight: 'bold' }} />
                <Tab value="ALL" label={t('academy.discount_validations.tab_all', discountRequests.length)} sx={{ minHeight: 44, textTransform: 'none', fontWeight: 'bold' }} />
              </Tabs>
            </Grid>

            <Grid item xs={12} md={5}>
              <TextField
                size="small"
                fullWidth
                placeholder={t('academy.discount_validations.search_placeholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                }}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Requests Table / Cards Display */}
        {filteredRequests.length === 0 ? (
          <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderRadius: 2.5 }}>
            <DiscountTrendIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography variant="subtitle1" fontWeight="bold" color="text.secondary">
              {t('academy.discount_validations.empty_title')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {activeTab === 'Pending' ? t('academy.discount_validations.empty_pending_desc') : t('academy.discount_validations.empty_filter_desc')}
            </Typography>
          </Paper>
        ) : (
          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2.5 }}>
            <Table>
              <TableHead sx={{ bgcolor: 'action.hover' }}>
                <TableRow>
                  <TableCell>{t('academy.discount_validations.col_date_requester')}</TableCell>
                  <TableCell>{t('academy.discount_validations.col_invoice_student')}</TableCell>
                  <TableCell>{t('academy.discount_validations.col_requested_discount')}</TableCell>
                  <TableCell>{t('academy.discount_validations.col_financial_impact')}</TableCell>
                  <TableCell>{t('academy.discount_validations.col_justification')}</TableCell>
                  <TableCell>{t('academy.discount_validations.col_status')}</TableCell>
                  <TableCell align="right">{t('academy.discount_validations.col_actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRequests.map((req) => {
                  return (
                    <TableRow key={req.id} hover>
                      {/* Date & Requester */}
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {req.request_date}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {t('academy.discount_validations.by_requester', req.requested_by_name || 'Utilisateur')}
                        </Typography>
                      </TableCell>

                      {/* Invoice & Student */}
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.lighter', color: 'primary.main', fontSize: 13 }}>
                            {(req.student_name || 'A')[0]}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" fontWeight="bold">
                              {req.student_name || 'Apprenant'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <InvoiceIcon fontSize="inherit" /> Facture {req.invoice_number || `#${req.invoice_id}`}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>

                      {/* Discount Details */}
                      <TableCell>
                        <Chip
                          icon={<DiscountIcon />}
                          label={`${req.discount_code || 'REMISE'} (-${req.discount_percent}%)`}
                          color="secondary"
                          size="small"
                          variant="outlined"
                          sx={{ fontWeight: 'bold' }}
                        />
                      </TableCell>

                      {/* Financial Impact */}
                      <TableCell>
                        <Typography variant="caption" color="text.secondary" sx={{ textDecoration: 'line-through', display: 'block' }}>
                          {t('academy.discount_validations.gross_label')}{currencySymbol}{req.original_amount}
                        </Typography>
                        <Typography variant="body2" fontWeight="bold" color="success.main">
                          {t('academy.discount_validations.net_label')}{currencySymbol}{req.new_amount}
                        </Typography>
                        <Typography variant="caption" color="secondary.main">
                          {t('academy.discount_validations.savings_label')}{currencySymbol}{req.discount_amount}
                        </Typography>
                      </TableCell>

                      {/* Justification */}
                      <TableCell sx={{ maxWidth: 260 }}>
                        <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                          "{req.justification || t('academy.discount_validations.no_reason')}"
                        </Typography>
                        {req.review_comment && (
                          <Typography variant="caption" color="error.main" sx={{ display: 'block', mt: 0.5 }}>
                            {t('academy.discount_validations.rejection_reason_label')}{req.review_comment}
                          </Typography>
                        )}
                      </TableCell>

                      {/* Status */}
                      <TableCell>
                        {req.status === 'Pending' && (
                          <Chip label={t('academy.discount_validations.status_pending')} color="warning" size="small" icon={<PendingIcon />} />
                        )}
                        {req.status === 'Approved' && (
                          <Box>
                            <Chip label={t('academy.discount_validations.status_approved')} color="success" size="small" icon={<CheckCircleIcon />} />
                            <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                              {req.reviewed_by_name} ({req.review_date})
                            </Typography>
                          </Box>
                        )}
                        {req.status === 'Rejected' && (
                          <Box>
                            <Chip label={t('academy.discount_validations.status_rejected')} color="error" size="small" icon={<CancelIcon />} />
                            <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                              {req.reviewed_by_name} ({req.review_date})
                            </Typography>
                          </Box>
                        )}
                      </TableCell>

                      {/* Action Decision Buttons */}
                      <TableCell align="right">
                        {req.status === 'Pending' ? (
                          <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <Button
                              size="small"
                              variant="contained"
                              color="success"
                              startIcon={<CheckCircleIcon />}
                              onClick={() => handleOpenApprove(req)}
                              sx={{ borderRadius: 2 }}
                            >
                              {t('academy.discount_validations.btn_approve')}
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              startIcon={<CancelIcon />}
                              onClick={() => handleOpenReject(req)}
                              sx={{ borderRadius: 2 }}
                            >
                              {t('academy.discount_validations.btn_reject')}
                            </Button>
                          </Stack>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            {t('academy.discount_validations.status_processed')}
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Approval Modal Dialog */}
        <Dialog open={openApproveModal} onClose={() => setOpenApproveModal(false)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircleIcon color="success" />
            <Typography variant="h6" fontWeight="bold">{t('academy.discount_validations.dialog_approve_title')}</Typography>
          </DialogTitle>
          <DialogContent dividers sx={{ p: 2.5 }}>
            {selectedRequest && (
              <Stack spacing={2}>
                <Alert severity="success" sx={{ borderRadius: 2 }}>
                  {t('academy.discount_validations.dialog_approve_alert',
                    selectedRequest.invoice_number,
                    `${currencySymbol}${selectedRequest.original_amount}`,
                    `${currencySymbol}${selectedRequest.new_amount}`,
                    selectedRequest.discount_percent
                  )}
                </Alert>

                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: 'action.hover' }}>
                  <Typography variant="caption" color="text.secondary" display="block">{t('academy.financial.discount.student_label')}</Typography>
                  <Typography variant="body2" fontWeight="bold">{selectedRequest.student_name}</Typography>

                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>{t('academy.discount_validations.col_justification')}</Typography>
                  <Typography variant="body2" sx={{ fontStyle: 'italic' }}>"{selectedRequest.justification}"</Typography>
                </Paper>
              </Stack>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setOpenApproveModal(false)} disabled={actionLoading}>
              {t('btn.cancel')}
            </Button>
            <Button
              variant="contained"
              color="success"
              startIcon={<CheckCircleIcon />}
              onClick={handleConfirmApprove}
              disabled={actionLoading}
            >
              {t('academy.discount_validations.dialog_approve_confirm')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Rejection Modal Dialog */}
        <Dialog open={openRejectModal} onClose={() => setOpenRejectModal(false)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CancelIcon color="error" />
            <Typography variant="h6" fontWeight="bold">{t('academy.discount_validations.dialog_reject_title')}</Typography>
          </DialogTitle>
          <DialogContent dividers sx={{ p: 2.5 }}>
            {selectedRequest && (
              <Stack spacing={2}>
                <Alert severity="warning" sx={{ borderRadius: 2 }}>
                  {t('academy.discount_validations.dialog_reject_alert',
                    selectedRequest.invoice_number,
                    `${currencySymbol}${selectedRequest.original_amount}`
                  )}
                </Alert>

                <TextField
                  required
                  multiline
                  rows={3}
                  label={t('academy.discount_validations.dialog_reject_field_label')}
                  placeholder={t('academy.discount_validations.dialog_reject_placeholder')}
                  value={rejectComment}
                  onChange={(e) => setRejectComment(e.target.value)}
                  fullWidth
                  error={!rejectComment.trim()}
                  helperText={!rejectComment.trim() ? t('academy.discount_validations.dialog_reject_required') : ''}
                />
              </Stack>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setOpenRejectModal(false)} disabled={actionLoading}>
              {t('btn.cancel')}
            </Button>
            <Button
              variant="contained"
              color="error"
              startIcon={<CancelIcon />}
              onClick={handleConfirmReject}
              disabled={actionLoading || !rejectComment.trim()}
            >
              {t('academy.discount_validations.dialog_reject_confirm')}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </GenericPage>
  );
};

export default DiscountValidationsPage;
